/**
 * Landing page the SwarmAI CLI opens to link an install to an account.
 *
 * `swarmai hub link` POSTs /api/v1/auth/install-link and opens the returned
 * URL here, with the install_id_hash carried in the `?install=` query param
 * (a cookie can't be used — the CLI, not the browser, made that POST). The
 * user enters their email, which fires the magic-link flow with
 * intent='link-install'; the device is bound on verify.
 */
import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { AuthShell } from './Login';
import { Alert, Button, Input } from '../components/ui';

const INSTALL_HASH_RE = /^[a-f0-9]{64}$/i;

export default function LinkDevice() {
  const [params] = useSearchParams();
  const installIdHash = params.get('install');
  const hashValid = !!installIdHash && INSTALL_HASH_RE.test(installIdHash);

  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!hashValid) return;
    setBusy(true);
    setError(null);
    try {
      // Pass the hash explicitly from the URL — the server reads it from
      // the request body, not a cookie.
      await api.startEmail(email.trim(), 'link-install', installIdHash!.toLowerCase());
      navigate('/auth/check-email', { state: { email: email.trim() } });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  if (!hashValid) {
    return (
      <AuthShell title="Link this device" subtitle="Connect the SwarmAI install on this machine to your Hub account.">
        <Alert kind="error">
          This page is missing a valid install reference. Open it by running{' '}
          <code className="text-slate-700">swarmai hub link</code> in your SwarmAI install — that command generates the
          correct link.
        </Alert>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Link this device" subtitle="Connect the SwarmAI install on this machine to your Hub account.">
      <form onSubmit={onSubmit} className="space-y-4">
        {error && <Alert kind="error">{error}</Alert>}
        <Input
          type="email"
          required
          autoFocus
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Button type="submit" disabled={busy} className="w-full">
          {busy ? 'Sending…' : 'Email me a link to confirm'}
        </Button>
      </form>
    </AuthShell>
  );
}
